import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { Dictionary, groupBy } from "lodash";
import { useCallback } from "react";
import { activityFeedKey } from "@/activity-feed/query";
import { useApi } from "@/core/api";
import { spiffApprovalsKey } from "@/spiff-approvals/query";
import { enrichUsers } from "@/spiff-approvals/utils";
import { useGetBusinessUsers } from "@/user/query";
import { DetailedUser, EnrichedUserWithSpiffs } from "@/user/user";
import {
  BulkDismissResponse,
  BulkDismissErrorResponse,
  ISpiff,
  BaseSpiff,
} from "./spiffs.d";
import { getEnrichedBaseSpiff, getEnrichedSpiff } from "./utils";

export const pendingSpiffsKey = (businessId?: string | null) =>
  ["pending-spiffs", businessId] as const;
export const dismissedSpiffsKey = (businessId?: string | null) =>
  ["dismissed-spiffs", businessId] as const;
export const approvedSpiffsKey = (businessId?: string | null) =>
  ["approved-spiffs", businessId] as const;
export const spiffKey = (businessId?: string | null, spiffId?: string | null) =>
  ["spiff", businessId, spiffId] as const;
export const userPendingSpiffsKey = (userId?: string | null) =>
  ["user-unapproved-spiffs", userId] as const;
export const userDismissedSpiffsKey = (userId?: string | null) =>
  ["user-dismissed-spiffs", userId] as const;

export const useGetPendingSpiffs = (
  businessId?: string | null,
): UseQueryResult<Dictionary<BaseSpiff[]>, Error> => {
  const { get } = useApi();

  return useQuery({
    queryKey: pendingSpiffsKey(businessId),
    queryFn: () =>
      get(`businesses/${businessId}/spiffs?scope=pending`)
        .then((res) => res.data.map(getEnrichedBaseSpiff))
        .then((spiffs) => groupBy(spiffs, "user_id")),
    enabled: !!businessId,
  });
};

export const useGetDismissedSpiffs = (
  businessId?: string | null,
): UseQueryResult<Dictionary<BaseSpiff[]>, Error> => {
  const { get } = useApi();

  return useQuery({
    queryKey: dismissedSpiffsKey(businessId),
    queryFn: () =>
      get(`businesses/${businessId}/spiffs?scope=dismissed`)
        .then((res) => res.data.map(getEnrichedBaseSpiff))
        .then((spiffs) => groupBy(spiffs, "user_id")),
    enabled: !!businessId,
  });
};

export const useGetApprovedSpiffs = (
  businessId?: string | null,
): UseQueryResult<Dictionary<BaseSpiff[]>, Error> => {
  const { get } = useApi();

  return useQuery({
    queryKey: approvedSpiffsKey(businessId),
    queryFn: () =>
      get(`businesses/${businessId}/spiffs?scope=approved`)
        .then((res) => res.data.map(getEnrichedBaseSpiff))
        .then((spiffs) => groupBy(spiffs, "user_id")),
    enabled: !!businessId,
  });
};

export const useGetUsersWithPendingSpiffs = (
  businessId?: string | null,
): UseQueryResult<EnrichedUserWithSpiffs[], Error> => {
  const {
    data: pendingSpiffs = {},
    isLoading: isLoadingPendingSpiffs,
    isError: isErrorPendingSpiffs,
    error: errorPendingSpiffs,
  } = useGetPendingSpiffs(businessId);

  const usersQuery = useGetBusinessUsers(businessId, {
    select: useCallback(
      (users: DetailedUser[]) => {
        const usersWithSpiffs = users
          .map((user) => ({
            ...user,
            spiffs: pendingSpiffs[user.id] || [],
          }))
          .filter((user) => user.spiffs.length > 0);

        return enrichUsers(usersWithSpiffs);
      },
      [pendingSpiffs],
    ),
  });

  return {
    ...usersQuery,
    isLoading: usersQuery.isLoading || isLoadingPendingSpiffs,
    isFetching: usersQuery.isFetching || isLoadingPendingSpiffs,
    isError: usersQuery.isError || isErrorPendingSpiffs,
    error: usersQuery.error || errorPendingSpiffs,
    isPending: usersQuery.isPending || isLoadingPendingSpiffs,
  } as UseQueryResult<EnrichedUserWithSpiffs[], Error>;
};

export const useGetUsersWithDismissedSpiffs = (
  businessId?: string | null,
): UseQueryResult<EnrichedUserWithSpiffs[], Error> => {
  const {
    data: dismissedSpiffs = {},
    isLoading: isLoadingDismissedSpiffs,
    isError: isErrorDismissedSpiffs,
    error: errorDismissedSpiffs,
  } = useGetDismissedSpiffs(businessId);

  const usersQuery = useGetBusinessUsers(businessId, {
    select: useCallback(
      (users: DetailedUser[]) => {
        const usersWithSpiffs = users
          .map((user) => ({
            ...user,
            spiffs: dismissedSpiffs[user.id] || [],
          }))
          .filter((user) => user.spiffs.length > 0);

        return enrichUsers(usersWithSpiffs);
      },
      [dismissedSpiffs],
    ),
  });

  return {
    ...usersQuery,
    isLoading: usersQuery.isLoading || isLoadingDismissedSpiffs,
    isFetching: usersQuery.isFetching || isLoadingDismissedSpiffs,
    isError: usersQuery.isError || isErrorDismissedSpiffs,
    error: usersQuery.error || errorDismissedSpiffs,
    isPending: usersQuery.isPending || isLoadingDismissedSpiffs,
  } as UseQueryResult<EnrichedUserWithSpiffs[], Error>;
};

export const useGetSpiff = (
  businessId?: string | null,
  spiffId?: string | null,
): UseQueryResult<ISpiff, Error> => {
  const { get } = useApi();

  return useQuery({
    queryKey: spiffKey(businessId, spiffId),
    queryFn: async () =>
      get(`businesses/${businessId}/spiffs/${spiffId}`).then((res) =>
        getEnrichedSpiff(res.data),
      ),
    enabled: !!businessId && !!spiffId,
  });
};

export const useGetUserPendingSpiffs = (
  userId?: string | null,
  options?: UseQueryOptions<ISpiff[], Error>,
): UseQueryResult<ISpiff[], Error> => {
  const { get } = useApi();

  return useQuery({
    queryKey: userPendingSpiffsKey(userId),
    queryFn: () =>
      get(`users/${userId}/spiffs?scope=pending`).then((res) =>
        res.data.map(getEnrichedSpiff),
      ),
    enabled: !!userId,
    ...options,
  });
};

export const useGetUserDismissedSpiffs = (
  userId?: string | null,
  options?: UseQueryOptions<ISpiff[], Error>,
): UseQueryResult<ISpiff[], Error> => {
  const { get } = useApi();

  return useQuery({
    queryKey: userDismissedSpiffsKey(userId),
    queryFn: () =>
      get(`users/${userId}/spiffs?scope=dismissed`).then((res) =>
        res.data.map(getEnrichedSpiff),
      ),
    enabled: !!userId,
    ...options,
  });
};

export const useDismissSpiff = (businessId?: string | null) => {
  const { patch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    BulkDismissResponse,
    BulkDismissErrorResponse,
    { spiffIds: string[] }
  >({
    mutationFn: ({ spiffIds }) =>
      patch(`businesses/${businessId}/spiffs/bulk_dismissal`, {
        spiff_ids: spiffIds,
      }).then((res) => res.data),
    onSuccess: (data: BulkDismissResponse) => {
      queryClient.invalidateQueries({ queryKey: activityFeedKey(businessId) });
      queryClient.invalidateQueries({ queryKey: pendingSpiffsKey(businessId) });
      queryClient.invalidateQueries({
        queryKey: dismissedSpiffsKey(businessId),
      });

      data.updated_spiffs.forEach((updatedSpiff) => {
        queryClient.invalidateQueries({
          queryKey: userPendingSpiffsKey(updatedSpiff.user_id),
        });
        queryClient.invalidateQueries({
          queryKey: userDismissedSpiffsKey(updatedSpiff.user_id),
        });
        queryClient.invalidateQueries({
          queryKey: spiffKey(businessId, updatedSpiff.id),
        });
      });
    },
  });
};

export const useBulkDismissSpiffs = (businessId?: string | null) => {
  const { patch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    BulkDismissResponse,
    BulkDismissErrorResponse,
    { spiffIds: string[] }
  >({
    mutationFn: ({ spiffIds }) =>
      patch(`businesses/${businessId}/spiffs/bulk_dismissal`, {
        spiff_ids: spiffIds,
      }).then((res) => res.data),
    onSuccess: (data: BulkDismissResponse) => {
      queryClient.invalidateQueries({ queryKey: pendingSpiffsKey(businessId) });
      queryClient.invalidateQueries({
        queryKey: dismissedSpiffsKey(businessId),
      });
      data.updated_spiffs.forEach((updatedSpiff) => {
        queryClient.invalidateQueries({
          queryKey: userPendingSpiffsKey(updatedSpiff.user_id),
        });
        queryClient.invalidateQueries({
          queryKey: userDismissedSpiffsKey(updatedSpiff.user_id),
        });
        queryClient.invalidateQueries({
          queryKey: spiffKey(businessId, updatedSpiff.id),
        });
      });
    },
  });
};

export const useReinstateSpiff = (businessId?: string | null) => {
  const { patch } = useApi();
  const queryClient = useQueryClient();

  return useMutation<ISpiff, Error, { spiffId: string }>({
    mutationFn: ({ spiffId }) =>
      patch(`businesses/${businessId}/spiffs/${spiffId}`, {
        reinstate: true,
      }).then((res) => res.data),
    onSuccess: (updatedSpiff: ISpiff) => {
      queryClient.invalidateQueries({ queryKey: activityFeedKey(businessId) });
      queryClient.invalidateQueries({ queryKey: pendingSpiffsKey(businessId) });
      queryClient.invalidateQueries({
        queryKey: dismissedSpiffsKey(businessId),
      });
      queryClient.invalidateQueries({
        queryKey: userPendingSpiffsKey(updatedSpiff.user_id),
      });
      queryClient.invalidateQueries({
        queryKey: userDismissedSpiffsKey(updatedSpiff.user_id),
      });
      queryClient.invalidateQueries({
        queryKey: spiffKey(businessId, updatedSpiff.id),
      });
    },
  });
};

export const useBulkApproveSpiffs = (businessId?: string | null) => {
  const { post } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    { updated_spiffs: ISpiff[]; spiff_approvals: Record<string, unknown>[] },
    Error,
    {
      spiffIds: string[];
      effectiveAt?: string;
      payPeriodStartDate?: string;
      payPeriodInclusiveEndDate?: string;
    }
  >({
    mutationFn: ({
      spiffIds,
      effectiveAt,
      payPeriodStartDate,
      payPeriodInclusiveEndDate,
    }) =>
      post(`businesses/${businessId}/spiff_approvals`, {
        spiff_ids: spiffIds,
        effective_at: effectiveAt,
        pay_period_start_date: payPeriodStartDate,
        pay_period_inclusive_end_date: payPeriodInclusiveEndDate,
      }).then((res) => res.data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: pendingSpiffsKey(businessId) });
      queryClient.invalidateQueries({
        queryKey: spiffApprovalsKey(businessId),
      });
      queryClient.invalidateQueries({
        queryKey: approvedSpiffsKey(businessId),
      });
      data.updated_spiffs.forEach((updatedSpiff) => {
        queryClient.invalidateQueries({
          queryKey: userPendingSpiffsKey(updatedSpiff.user_id),
        });
        queryClient.invalidateQueries({
          queryKey: spiffKey(businessId, updatedSpiff.id),
        });
      });
    },
  });
};

export const useCreateSpiff = (businessId?: string | null) => {
  const { post } = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    ISpiff,
    Error,
    {
      userId: string;
      triggerId: string;
      triggerType: string;
      event: string;
      percentageBaseAmountCents?: number;
    }
  >({
    mutationFn: ({
      userId,
      triggerId,
      triggerType,
      event,
      percentageBaseAmountCents,
    }) =>
      post(`businesses/${businessId}/spiffs`, {
        user_id: userId,
        trigger_id: triggerId,
        trigger_type: triggerType,
        event,
        percentage_base_amount_cents: percentageBaseAmountCents,
      }).then((res) => res.data),
    onSuccess: (createdSpiff: ISpiff) => {
      queryClient.invalidateQueries({ queryKey: pendingSpiffsKey(businessId) });
      queryClient.invalidateQueries({ queryKey: activityFeedKey(businessId) });
      queryClient.invalidateQueries({
        queryKey: userPendingSpiffsKey(createdSpiff.user_id),
      });
    },
  });
};
